import type { Assignment } from "@/schemas/assignment";
import type { ShiftResponse } from "@/schemas/shift";
import type { WorkerUpcomingShift } from "@/queries/api/workerDashboardApi";

// Map an Assignment (API) to a ShiftCard (UI)
import { format } from "date-fns";
import { ROLE_OPTIONS as roles } from "@/constants/roles";

// This is the UI type for a "shift" event (what the user sees)
export type ShiftCard = {
  id: string;
  name: string;
  roleLabel: string;
  roleValue: string;
  ratePerHour: number;
  total?: number;
  dateLabel: string;
  timeLabel: string;
  venue: string;
  roleIconValue: string;
  businessId?: number;
  businessUserId?: string;
  businessName?: string;
  workerId?: string;
  workerName?: string;
  description?: string;
  assignmentStatus?: string;
  startDateTime?: string;
  /** Worker view: a not-yet-booked shift (employer offer awaiting the worker, or the worker's
   *  application awaiting approval). Clicking opens the shift modal instead of the sidebar. */
  pendingStatus?: "offered" | "applied";
  shiftId?: number;
};

export function assignmentToCard(assignment: Assignment): ShiftCard {
  const shift = assignment.shift;
  // Handle both string and object for start_time/end_time
  let startHour = "";
  let startMinute = "";
  let endHour = "";
  let endMinute = "";
  if (typeof shift.start_time === "string") {
    const parts = shift.start_time.split(":");
    startHour = parts[0] || "";
    startMinute = parts[1] || "";
  } else {
    startHour = "";
    startMinute = "";
  }
  if (typeof shift.end_time === "string") {
    const parts = shift.end_time.split(":");
    endHour = parts[0] || "";
    endMinute = parts[1] || "";
  } else {
    endHour = "";
    endMinute = "";
  }
  // Format dateLabel as "MMM d, yyyy"
  let formattedDate = "";
  if (assignment.start_date_time) {
    try {
      formattedDate = format(
        new Date(assignment.start_date_time),
        "MMM d, yyyy",
      );
    } catch {
      formattedDate = assignment.start_date_time.split("T")[0];
    }
  }
  const roleValue = shift.role_name
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  const roleObj = roles.find((r) => r.value === roleValue);
  const roleLabel = roleObj ? roleObj.label : shift.role_name;
  // Prefer shift.location for venue
  const venue =
    shift.location || shift.address.address1 || shift.address.location || "";
  // Business name and worker name
  const businessName = assignment.business?.name || "";
  const workerName = assignment.worker?.full_name || "Worker";

  return {
    id: String(assignment.id),
    name: shift.name || shift.role_name,
    roleLabel,
    roleValue,
    ratePerHour: assignment.wage,
    dateLabel: formattedDate,
    timeLabel: `${startHour}:${startMinute} - ${endHour}:${endMinute}`,
    venue,
    roleIconValue: roleValue,
    businessId: assignment.business?.id,
    businessUserId: assignment.business?.user_id,
    businessName,
    workerId: assignment.worker_id,
    workerName,
    description: shift.role_description || undefined,
    assignmentStatus: assignment.assignment_status,
    startDateTime: assignment.start_date_time,
  };
}

// Map a Shift (API) to a ShiftCard for the employer schedule view.
// Unlike assignmentToCard, the shift may not yet have an assigned worker.
export function shiftToScheduleCard(shift: ShiftResponse): ShiftCard {
  const roleValue = shift.role_name
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  const roleObj = roles.find((r) => r.value === roleValue);
  const roleLabel = roleObj ? roleObj.label : shift.role_name;

  let formattedDate = "";
  if (shift.start_date) {
    try {
      formattedDate = format(new Date(shift.start_date + "T00:00:00"), "MMM d, yyyy");
    } catch {
      formattedDate = shift.start_date;
    }
  }

  const venue = shift.location || shift.address.address1 || shift.address.location || "";
  const startTime = typeof shift.start_time === "string" ? shift.start_time : "";
  const endTime = typeof shift.end_time === "string" ? shift.end_time : "";
  const [startHour = "", startMinute = ""] = startTime.split(":");
  const [endHour = "", endMinute = ""] = endTime.split(":");
  const startDateTime = shift.start_date && startTime
    ? `${shift.start_date}T${startTime}`
    : undefined;

  return {
    id: String(shift.id),
    name: shift.name || shift.role_name,
    roleLabel,
    roleValue,
    ratePerHour: shift.wage,
    dateLabel: formattedDate,
    timeLabel: `${startHour}:${startMinute} - ${endHour}:${endMinute}`,
    venue,
    roleIconValue: roleValue,
    businessId: shift.business.id,
    businessUserId: shift.business.user_id,
    businessName: shift.business.name || "",
    workerId: undefined,
    workerName: undefined,
    description: shift.role_description || undefined,
    assignmentStatus: shift.status,
    startDateTime,
  };
}

// Map a worker's pending (offered / applied) upcoming shift to a ShiftCard for the schedule list.
export function upcomingToCard(u: WorkerUpcomingShift): ShiftCard {
  const roleName = u.role ?? "";
  const roleValue = roleName.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  const roleObj = roles.find((r) => r.value === roleValue);
  const start = u.start_at ? new Date(u.start_at) : null;
  const end = u.end_at ? new Date(u.end_at) : null;
  const valid = (d: Date | null): d is Date => d != null && !Number.isNaN(d.getTime());
  return {
    id: `pending-${u.shift_id}`,
    shiftId: u.shift_id,
    name: roleObj ? roleObj.label : roleName,
    roleLabel: roleObj ? roleObj.label : roleName,
    roleValue,
    ratePerHour: u.rate ?? 0,
    total: u.estimated_pay ?? undefined,
    dateLabel: valid(start) ? format(start, "MMM d, yyyy") : "",
    timeLabel: valid(start) && valid(end) ? `${format(start, "HH:mm")} - ${format(end, "HH:mm")}` : "",
    venue: u.restaurant?.address ?? u.restaurant?.name ?? "",
    roleIconValue: roleValue,
    businessId: u.restaurant?.id ?? undefined,
    businessName: u.restaurant?.name ?? "",
    startDateTime: u.start_at ?? undefined,
    pendingStatus: u.status === "offered" ? "offered" : "applied",
  };
}
